import Invoice from "../../models/domain/Invoice";

interface Props {
  invoices: Invoice[];
  totals: {
    billed: number;
    paid: number;
    balance: number;
  };
  readOnly?: boolean;
  onCollectClick?: (invoice: Invoice) => void;
}

export function InvoiceTable({
  invoices,
  totals,
  readOnly = false,
  onCollectClick,
}: Props) {
  return (
    <div className="card shadow-sm border-0">
      <div className="table-responsive">
        <table
          className="table align-middle mb-0"
          style={{ tableLayout: "fixed", width: "100%" }}
        >
          <thead className="table-light">
            <tr className="small text-uppercase text-muted">
              <th style={{ width: "130px" }}>Invoice #</th>
              <th style={{ width: "190px" }}>Patient Name</th>
              <th style={{ width: "140px" }}>Doctor</th>
              <th style={{ width: "250px" }}>Service</th>
              <th style={{ width: "100px" }}>Mode</th>
              <th style={{ width: "150px" }}>Status</th>
              <th className="text-end" style={{ width: "80px" }}>
                Billed
              </th>
              <th className="text-end" style={{ width: "80px" }}>
                Paid
              </th>
              <th className="text-end" style={{ width: "80px" }}>
                Balance
              </th>
            </tr>
          </thead>

          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.receipt_no}>
                <td
                  className="fw-semibold text-danger"
                  style={{ wordBreak: "break-word" }}
                >
                  #{inv.receipt_no}
                </td>

                <td
                  style={{ wordBreak: "break-word" }}
                  title={inv.patient_name}
                >
                  {inv.patient_name}
                </td>

                <td style={{ wordBreak: "break-word" }} title={inv.doctor_name}>
                  {inv.doctor_name}
                </td>

                <td
                  style={{ wordBreak: "break-word" }}
                  title={
                    inv.particulars?.map((p) => p.description).join(", ") ||
                    inv.consult_description
                  }
                >
                  {inv.particulars?.map((p) => p.description).join(", ") ||
                    inv.consult_description}
                </td>

                <td style={{ wordBreak: "break-word" }}>
                  {inv.payment_mode ?? "-"}
                </td>

                <td
                  style={{ wordBreak: "break-word" }}
                  className="align-middle"
                >
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span
                      className={`badge ${
                        inv.payment_status === "FULL"
                          ? "bg-success-subtle text-success"
                          : "bg-warning-subtle text-warning"
                      }`}
                    >
                      {inv.payment_status}
                    </span>

                    {!readOnly &&
                      inv.payment_status === "PARTIAL" &&
                      onCollectClick && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary py-0 px-2"
                          style={{ fontSize: "12px", lineHeight: "1.2" }}
                          onClick={() => onCollectClick(inv)}
                        >
                          Collect
                        </button>
                      )}
                  </div>
                </td>

                <td className="text-end" style={{ wordBreak: "break-word" }}>
                  ₹{inv.total_amount?.toLocaleString()}
                </td>

                <td
                  className="text-end text-success"
                  style={{ wordBreak: "break-word" }}
                >
                  ₹{inv.paid_amount?.toLocaleString()}
                </td>

                <td
                  className={`text-end fw-semibold ${
                    inv.pending_amount > 0 ? "text-danger" : ""
                  }`}
                  style={{ wordBreak: "break-word" }}
                >
                  ₹{inv.pending_amount?.toLocaleString()}
                </td>
              </tr>
            ))}

            {/* Totals Row */}
            <tr className="table-light fw-semibold">
              <td colSpan={6} className="text-end">
                TOTALS
              </td>

              <td className="text-end" style={{ wordBreak: "break-word" }}>
                ₹{totals.billed?.toLocaleString()}
              </td>

              <td
                className="text-end text-info"
                style={{ wordBreak: "break-word" }}
              >
                ₹{totals.paid?.toLocaleString()}
              </td>

              <td
                className="text-end text-danger"
                style={{ wordBreak: "break-word" }}
              >
                ₹{totals.balance?.toLocaleString()}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
